import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api.js';
import BankExports from '@/components/money/BankExports.jsx';

// R36 "MONEY THIS WEEK" (NIGHT-13, David 2026-11-02): the money home. What to pay, sweep and move this week, which
// account is short and by when, how fresh each bank export is, and what does not add up. Every sentence is the server's
// (utils/money-this-week.js over the payables, the trust number, the shortfall and the sweep queues); each item carries
// the one door that acts on it. This screen derives nothing.

function Action({ a }) {
  if (!a) return null;
  return <Link to={a.to} className="shrink-0 rounded-card border border-border px-3 py-1.5 text-sm text-accent hover:bg-bg">{a.label}</Link>;
}

function Item({ it, danger = false, children }) {
  return (
    <li className="py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className={`min-w-0 flex-1 text-sm ${danger ? 'text-danger' : 'text-text'}`}>{it.words}</p>
        <Action a={it.action} />
      </div>
      {children}
    </li>
  );
}

function Section({ title, empty, items, render }) {
  return (
    <section className="mt-6">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-text-dim">{title}</h3>
      {items && items.length ? <ul className="mt-1 divide-y divide-border rounded-card border border-border bg-surface px-4">{items.map(render)}</ul>
        : <p className="mt-2 text-sm text-text-muted">{empty}</p>}
    </section>
  );
}

export default function MoneyWeek() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const refresh = useCallback(async () => {
    try { setData(await api.get('/api/money/this-week')); setError(''); }
    catch (e) { setError(e.message || 'Could not work out this week'); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="p-4">
      <h2 className="text-xl font-semibold text-text">Money this week</h2>
      <p className="mt-1 text-sm text-text-muted">What to pay, sweep and move, which account is short, and what does not add up. Each line has the button that does it.</p>
      {error ? <p className="mt-4 text-sm text-danger">{error}</p> : null}
      {data === null && !error ? <p className="mt-4 text-sm text-text-muted">Working it out&hellip;</p> : null}
      {data ? (
        <>
          <Section title="Pay" empty="No confirmed statement line is owed." items={data.pay} render={(it) => (
            <Item key={it.statement_id} it={it}>
              {it.from.length ? (
                <ul className="mt-1 space-y-0.5 text-xs text-text-muted">{it.from.map((f) => <li key={f.account}>{f.words}</li>)}</ul>
              ) : null}
              {it.not_covered.length ? <ul className="mt-1 space-y-0.5 text-xs text-danger">{it.not_covered.map((n, i) => <li key={i}>{n.words}</li>)}</ul> : null}
              {it.no_account.length ? <ul className="mt-1 space-y-0.5 text-xs text-danger">{it.no_account.map((n, i) => <li key={i}>{n.words}</li>)}</ul> : null}
            </Item>
          )} />
          <Section title="Sweep" empty="No trust account has a sweep queue." items={data.sweep} render={(it) => <Item key={it.account} it={it} danger={!it.proposes && it.ready_total > 0} />} />
          <Section title="Move between accounts" empty="No account owes another." items={data.move} render={(it, i) => <Item key={i} it={it} />} />
          <Section title="Short" empty="No trust account is short of what its statements say it must pay." items={data.short} render={(it) => <Item key={it.account} it={it} danger />} />
          <section className="mt-6">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-text-dim">Exports</h3>
            {/* R36.5 and R39 together: each account's last loaded day (stale after 7 days, the server's) and the drop zone. */}
            <BankExports onLoaded={refresh} />
          </section>
          <Section title="Problems" empty="Nothing that does not add up." items={data.problems} render={(it, i) => <Item key={i} it={it} danger />} />
        </>
      ) : null}
    </div>
  );
}
